import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import { getImageSrc } from '../../utils/cmsAPI';

// Import interview theme colors for modern styling
const interviewTheme = {
  colors: {
    primary: '#6366f1', // Indigo
    primaryDark: '#4f46e5',
    primaryLight: '#818cf8',
    secondary: '#8b5cf6', // Purple
    secondaryDark: '#7c3aed',
    secondaryLight: '#a78bfa',
    accent: '#ec4899', // Pink accent
    background: '#f8fafc',
    surface: '#ffffff',
    text: '#1e293b',
    textLight: '#64748b',
    textMuted: '#94a3b8',
    border: '#e2e8f0',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
    header: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    subtle: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
  },
  shadows: {
    sm: '0 1px 2px rgba(99, 102, 241, 0.1)',
    md: '0 4px 6px rgba(99, 102, 241, 0.1)',
    lg: '0 10px 15px rgba(99, 102, 241, 0.1)',
  },
};

const SharedDrawer = ({
  open,
  onClose,
  title,
  content,
  navigationContent = null,
  headerActions = null,
  image = null,
  about = null,
  mission = null,
  team = null
}) => {
  const theme = useTheme();
  const [currentContent, setCurrentContent] = useState(content);
  const [showNavigation, setShowNavigation] = useState(true);
  const drawerRef = useRef(null);

  // Determine if we're on desktop
  const isDesktop = useMediaQuery('(min-width:768px)');

  useEffect(() => {
    setCurrentContent(content);
  }, [content]);

  // On open, scroll drawer to top
  // Also scroll to top when navigating to a project (currentContent changes)
  useEffect(() => {
    if (open && drawerRef.current) {
      const drawerPaper = drawerRef.current.querySelector('.MuiDrawer-paper');
      if (drawerPaper) {
        // Use double requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            drawerPaper.scrollTop = 0;
            drawerPaper.scrollTo({ top: 0, behavior: 'auto' });
          });
        });
      }
    }
  }, [open, currentContent, showNavigation]);

  // Note: MUI Drawer handles body scroll lock automatically
  // No manual scroll lock needed

  const handleNavigation = (newContent) => {
    setCurrentContent(newContent);
    setShowNavigation(false);
    // Scroll drawer to top when navigating to a project
    // Use double requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (drawerRef.current) {
          const drawerPaper = drawerRef.current.querySelector('.MuiDrawer-paper');
          if (drawerPaper) {
            drawerPaper.scrollTop = 0;
            // Force scroll to top immediately as well
            drawerPaper.scrollTo({ top: 0, behavior: 'auto' });
          }
        }
      });
    });
  };

  const handleBack = () => {
    setShowNavigation(true);
    setCurrentContent(content);
    // Scroll drawer to top when going back
    // Use setTimeout to ensure state update and DOM render first
    setTimeout(() => {
      if (drawerRef.current) {
        const drawerPaper = drawerRef.current.querySelector('.MuiDrawer-paper');
        if (drawerPaper) {
          drawerPaper.scrollTop = 0;
        }
      }
    }, 0);
  };

  return (
    <Drawer
      anchor={isDesktop ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      ref={drawerRef}
      PaperProps={{
        sx: {
          // Keep dynamic sizing
          width: isDesktop ? '65%' : '100%',
          height: isDesktop ? '100vh' : '85vh',
          // Preserve unique border radius for desktop vs mobile
          borderRadius: isDesktop ? '16px 0 0 16px' : '16px 16px 0 0'
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            transition: 'opacity 200ms cubic-bezier(0.32, 0.72, 0, 1)'
          }
        }
      }}
    >
      {/* HEADER AREA */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: interviewTheme.gradients.header,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: interviewTheme.shadows.md,
        }}
      >
        {!showNavigation && (
          <IconButton
            onClick={handleBack}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography
          variant="h6"
          sx={{
            flex: 1,
            textAlign: 'center',
            color: 'white',
            px: 6,
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '0.5px',
            fontFamily: '"museo-sans", sans-serif',
          }}
        >
          {!showNavigation ? title : navigationContent?.title || title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENT AREA */}
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          backgroundColor: interviewTheme.colors.background,
          minHeight: '100%',
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: interviewTheme.shadows.md,
          }
        }}
      >
        {showNavigation && navigationContent ? (
          <>
            {about && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: interviewTheme.colors.primary,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      mb: 1.5,
                      fontFamily: '"museo-sans", sans-serif',
                    }}
                  >
                    About
                  </Typography>
                  <Typography
                    sx={{
                      color: interviewTheme.colors.text,
                      lineHeight: 1.7,
                      fontSize: '1rem',
                    }}
                  >
                    {about}
                  </Typography>
                </Box>
                <Divider sx={{ my: 3, borderColor: interviewTheme.colors.border }} />
              </>
            )}

            {mission && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: interviewTheme.colors.primary,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      mb: 1.5,
                      fontFamily: '"museo-sans", sans-serif',
                    }}
                  >
                    Mission
                  </Typography>
                  <Typography
                    sx={{
                      color: interviewTheme.colors.text,
                      lineHeight: 1.7,
                      fontSize: '1rem',
                    }}
                  >
                    {mission}
                  </Typography>
                </Box>
                <Divider sx={{ my: 3, borderColor: interviewTheme.colors.border }} />
              </>
            )}

            {team && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: interviewTheme.colors.primary,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      mb: 1.5,
                      fontFamily: '"museo-sans", sans-serif',
                    }}
                  >
                    Team
                  </Typography>
                  {team}
                </Box>
                <Divider sx={{ my: 3, borderColor: interviewTheme.colors.border }} />
              </>
            )}

            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                color: interviewTheme.colors.primary,
                fontWeight: 700,
                fontSize: '1.25rem',
                mb: 3,
                fontFamily: '"museo-sans", sans-serif',
              }}
            >
              {navigationContent.title || 'Projects'}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 2.5,
                mt: 2
              }}
            >
              {navigationContent.items.map((item, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      handleNavigation(item.content);
                    }
                  }}
                  sx={{
                    background: interviewTheme.gradients.subtle,
                    border: `1px solid ${interviewTheme.colors.border}`,
                    p: 3,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textAlign: 'center',
                    boxShadow: interviewTheme.shadows.sm,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: interviewTheme.gradients.header,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: interviewTheme.shadows.lg,
                      borderColor: interviewTheme.colors.primaryLight,
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                    }
                  }}
                >
                  <Typography
                    sx={{
                      color: interviewTheme.colors.text,
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      lineHeight: 1.5,
                      fontFamily: '"museo-sans", sans-serif',
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ width: '100%', maxWidth: '800px', mx: 'auto' }}>
            {image && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 3
                }}
              >
                <Box
                  component="img"
                  src={getImageSrc(image)}
                  alt={title}
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: 1,
                    objectFit: 'contain'
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                // Headings
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 700,
                  marginTop: '2.5rem',
                  marginBottom: '1.5rem',
                  color: interviewTheme.colors.primary,
                  fontFamily: '"museo-sans", sans-serif',
                  lineHeight: 1.3,
                },
                '& h2': {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  marginTop: '2.5rem',
                  marginBottom: '1.25rem',
                  color: interviewTheme.colors.primary,
                  fontFamily: '"museo-sans", sans-serif',
                  lineHeight: 1.3,
                },
                '& h3': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  marginTop: '2rem',
                  marginBottom: '1rem',
                  color: interviewTheme.colors.primaryDark,
                  fontFamily: '"museo-sans", sans-serif',
                  lineHeight: 1.4,
                },
                '& h4': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginTop: '1.5rem',
                  marginBottom: '0.75rem',
                  color: interviewTheme.colors.primaryDark,
                  fontFamily: '"museo-sans", sans-serif',
                  lineHeight: 1.4,
                },
                // Paragraphs
                '& p': {
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  marginBottom: '1.25rem',
                  color: interviewTheme.colors.text,
                  fontFamily: '"museo-sans", sans-serif',
                },
                // Unordered lists
                '& ul': {
                  listStyleType: 'disc',
                  marginLeft: 0,
                  marginTop: '1rem',
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                  '& ul': {
                    listStyleType: 'circle',
                    marginTop: '0.5rem',
                    marginBottom: '0.5rem',
                    paddingLeft: '1.5rem',
                  },
                  '& ul ul': {
                    listStyleType: 'square',
                  },
                },
                // Ordered lists
                '& ol': {
                  listStyleType: 'decimal',
                  marginLeft: 0,
                  marginTop: '1rem',
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                  counterReset: 'list-counter',
                  '& ol': {
                    listStyleType: 'lower-alpha',
                    marginTop: '0.5rem',
                    marginBottom: '0.5rem',
                    paddingLeft: '1.5rem',
                  },
                  '& ol ol': {
                    listStyleType: 'lower-roman',
                  },
                },
                // List items
                '& li': {
                  marginBottom: '0.75rem',
                  paddingLeft: '0.5rem',
                  lineHeight: 1.8,
                  fontSize: '1rem',
                  color: interviewTheme.colors.text,
                  fontFamily: '"museo-sans", sans-serif',
                  display: 'list-item',
                  '& p': {
                    marginBottom: '0.5rem',
                    display: 'inline',
                  },
                  '& p:last-child': {
                    marginBottom: 0,
                  },
                  '&::marker': {
                    color: interviewTheme.colors.primary,
                    fontWeight: 600,
                  },
                  // Nested lists within list items
                  '& ul, & ol': {
                    marginTop: '0.75rem',
                    marginBottom: '0.75rem',
                  },
                },
                // Strong/bold text
                '& strong': {
                  color: interviewTheme.colors.primary,
                  fontWeight: 700,
                },
                // Links
                '& a': {
                  color: interviewTheme.colors.primary,
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: interviewTheme.colors.primaryDark,
                  },
                },
                // Code blocks
                '& code': {
                  backgroundColor: interviewTheme.colors.background,
                  padding: '0.2rem 0.4rem',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontFamily: 'monospace',
                  color: interviewTheme.colors.primaryDark,
                },
                '& pre': {
                  backgroundColor: interviewTheme.colors.background,
                  padding: '1rem',
                  borderRadius: '8px',
                  overflow: 'auto',
                  marginBottom: '1.5rem',
                  '& code': {
                    backgroundColor: 'transparent',
                    padding: 0,
                  },
                },
                // Blockquotes
                '& blockquote': {
                  borderLeft: `4px solid ${interviewTheme.colors.primaryLight}`,
                  paddingLeft: '1rem',
                  marginLeft: 0,
                  marginTop: '1rem',
                  marginBottom: '1.5rem',
                  fontStyle: 'italic',
                  color: interviewTheme.colors.textLight,
                },
              }}
            >
              <ReactMarkdown>{currentContent || ''}</ReactMarkdown>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default SharedDrawer;